import { useState, useEffect } from "react";
import { api, type AuthUser } from "@/lib/api";

const ROLES = ["admin", "marketing", "instructor", "evaluador", "estudiante"] as const;

export default function UsuariosPage() {
  const [users, setUsers] = useState<AuthUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    password: "",
    role: "instructor",
    company: "",
    phone: "",
    can_evaluate: false,
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const load = () => {
    setLoading(true);
    api.getUsers().then(setUsers).catch(() => setUsers([])).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const staff = users.filter((u) => u.role !== "estudiante");

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    try {
      await api.createUser(form);
      setForm({
        full_name: "",
        email: "",
        password: "",
        role: "instructor",
        company: "",
        phone: "",
        can_evaluate: false,
      });
      setSuccess("Usuario creado correctamente. Se le ha enviado un correo con su contraseña temporal.");
      load();
      setTimeout(() => setSuccess(""), 5000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error");
    }
  };

  const toggleActive = async (u: AuthUser) => {
    await api.updateUser(u.id, { active: !u.active });
    load();
  };

  const handleRoleChange = async (u: AuthUser, newRole: string) => {
    await api.updateUser(u.id, { role: newRole });
    load();
  };

  const handleFieldBlur = async (u: AuthUser, field: "company" | "phone", value: string) => {
    const current = (u[field] || "").trim();
    const next = value.trim();
    if (current === next) return;
    try {
      await api.updateUser(u.id, { [field]: next });
      load();
    } catch (err) {
      alert(err instanceof Error ? err.message : "No se pudo actualizar");
      load();
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Usuarios del sistema</h1>
        <p className="text-sm text-[var(--color-text-muted)]">Administradores, marketing, instructores y evaluadores</p>
      </div>
      <form onSubmit={handleCreate} className="card grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        <h2 className="md:col-span-full font-semibold text-sm">Nuevo usuario</h2>
        <input className="input-field" placeholder="Nombre completo" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} required />
        <input type="email" className="input-field" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
        <input type="password" className="input-field" placeholder="Contraseña" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />
        <input className="input-field" placeholder="Empresa (opcional)" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
        <input className="input-field" placeholder="Teléfono (opcional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        <select
          className="input-field"
          value={form.role}
          onChange={(e) => {
            const role = e.target.value;
            setForm({
              ...form,
              role,
              can_evaluate: role === "instructor" ? form.can_evaluate : false,
            });
          }}
        >
          <option value="super_admin">super_admin</option>
          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        {form.role === "instructor" ? (
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.can_evaluate} onChange={(e) => setForm({ ...form, can_evaluate: e.target.checked })} />
            Puede evaluar (acceso al portal evaluador)
          </label>
        ) : (
          <p className="text-xs text-[var(--color-text-muted)] self-center">
            “Puede evaluar” solo aplica al rol instructor.
          </p>
        )}
        <button type="submit" className="btn-primary cursor-pointer">Crear usuario</button>
        {error && <p className="md:col-span-full text-red-400 text-sm">{error}</p>}
        {success && <p className="md:col-span-full text-green-500 text-sm">{success}</p>}
      </form>
      {loading ? (
        <div className="text-center py-12"><i className="ri-loader-4-line animate-spin text-2xl"></i></div>
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[var(--color-text-muted)] border-b border-[var(--color-border)]">
                <th className="pb-3 pr-3">Nombre</th>
                <th className="pb-3 pr-3">Email</th>
                <th className="pb-3 pr-3">Empresa</th>
                <th className="pb-3 pr-3">Teléfono</th>
                <th className="pb-3 pr-3">Rol</th>
                <th className="pb-3 pr-3">Estado</th>
                <th className="pb-3">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {staff.map((u) => (
                <tr key={u.id} className="border-b border-[var(--color-border)]/50">
                  <td className="py-3 pr-3 font-medium">{u.full_name}</td>
                  <td className="py-3 pr-3 text-[var(--color-text-secondary)]">{u.email}</td>
                  <td className="py-3 pr-3">
                    <input
                      className="input-field py-1.5 text-xs min-w-[120px]"
                      defaultValue={u.company || ""}
                      placeholder="—"
                      onBlur={(e) => handleFieldBlur(u, "company", e.target.value)}
                      aria-label={`Empresa de ${u.full_name}`}
                    />
                  </td>
                  <td className="py-3 pr-3">
                    <input
                      className="input-field py-1.5 text-xs min-w-[110px]"
                      defaultValue={u.phone || ""}
                      placeholder="—"
                      onBlur={(e) => handleFieldBlur(u, "phone", e.target.value)}
                      aria-label={`Teléfono de ${u.full_name}`}
                    />
                  </td>
                  <td className="py-3 pr-3">
                    <select
                      className="bg-[var(--color-bg-tertiary)] border border-[var(--color-border)] rounded px-2 py-1 text-xs"
                      value={u.role}
                      onChange={(e) => handleRoleChange(u, e.target.value)}
                    >
                      <option value="super_admin">super_admin</option>
                      {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </td>
                  <td className="py-3 pr-3">{u.active ? <span className="badge-success">Activo</span> : <span className="badge-danger">Inactivo</span>}</td>
                  <td className="py-3 flex items-center gap-2">
                    <button onClick={() => toggleActive(u)} className="text-xs text-[var(--color-accent)] cursor-pointer hover:underline">
                      {u.active ? "Desactivar" : "Activar"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
